import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ProductFilter from '../../components/ui/ProductFilter';
import ProductSearch from './components/ProductSearch';
import CategoryFilter from './components/CategoryFilter';
import ProductGrid from './components/ProductGrid';
import Icon from '../../components/AppIcon';

const ProductPortfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Complete product data from Endosurgery's catalog
  const mockProducts = [
    // ERCP Products (6)
    {
      id: 1,
      name: "Papillotome/Sphincterotome",
      description: "Precision papillotome and sphincterotome for ERCP procedures.",
      category: "ERCP",
      manufacturer: "Endosurgery",
      model: "PAP-SPH-001",
      image: "/assets/images/papillotome-sphincterotome.jpg",
      price: 850,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        wireSize: "0.025 inch",
        workingLength: "200cm",
        cuttingLength: "20mm"
      }
    },
    {
      id: 2,
      name: "Needle Knife",
      description: "Advanced needle knife for precise cutting during ERCP interventions.",
      category: "ERCP",
      manufacturer: "Endosurgery",
      model: "NK-001",
      image: "/assets/images/needle-knife.jpg",
      price: 650,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        wireSize: "0.018 inch",
        workingLength: "200cm",
        cuttingLength: "5mm"
      }
    },
    {
      id: 3,
      name: "Guidewires",
      description: "High-quality guidewires for ERCP navigation and procedures.",
      category: "ERCP",
      manufacturer: "Endosurgery",
      model: "GW-ERCP-001",
      image: "/assets/images/guidewires.jpg",
      price: 120,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "0.025-0.035 inch",
        length: "400-480cm",
        material: "Nitinol core"
      }
    },
    {
      id: 4,
      name: "Stone Removal Balloon",
      description: "Specialized balloon for stone removal during ERCP procedures.",
      category: "ERCP",
      manufacturer: "Endosurgery",
      model: "SRB-001",
      image: "/assets/images/stone-removal-balloon.jpg",
      price: 450,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        balloonSize: "8-12mm",
        workingLength: "200cm",
        inflationVolume: "2-4ml"
      }
    },
    {
      id: 5,
      name: "Cannula",
      description: "Professional cannula for ERCP access and procedures.",
      category: "ERCP",
      manufacturer: "Endosurgery",
      model: "CAN-ERCP-001",
      image: "/assets/images/cannula.jpg",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "5-7Fr",
        workingLength: "200cm",
        material: "Teflon"
      }
    },
    {
      id: 6,
      name: "Dilatation",
      description: "Dilatation devices for ERCP therapeutic procedures.",
      category: "ERCP",
      manufacturer: "Endosurgery",
      model: "DIL-ERCP-001",
      image: "/assets/images/dilatation.jpg",
      price: 320,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        balloonSize: "6-10mm",
        workingLength: "200cm",
        inflationPressure: "8-12atm"
      }
    },

    // Gastrostomy Products (2)
    {
      id: 7,
      name: "PEG Kit",
      description: "Complete PEG (Percutaneous Endoscopic Gastrostomy) kit system.",
      category: "Gastrostomy",
      manufacturer: "Endosurgery",
      model: "PEG-KIT-001",
      image: "/assets/images/peg-kit.jpg",
      price: 850,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        tubeSize: "20-24Fr",
        length: "25cm",
        balloonCapacity: "20ml"
      }
    },
    {
      id: 8,
      name: "PEG Replacement Tube",
      description: "Replacement tubes for PEG procedures and maintenance.",
      category: "Gastrostomy",
      manufacturer: "Endosurgery",
      model: "PEG-RT-001",
      image: "/assets/images/peg-replacement-tube.jpg",
      price: 280,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        tubeSize: "20-24Fr",
        length: "25cm",
        material: "Silicone"
      }
    },

    // Hemostasis Products (3)
    {
      id: 9,
      name: "Hemoclip",
      description: "Hemostatic clips for bleeding control during procedures.",
      category: "Hemostasis",
      manufacturer: "Endosurgery",
      model: "HC-001",
      image: "/assets/images/hemoclip.jpg",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        clipSize: "Medium",
        workingLength: "230cm",
        material: "Titanium"
      }
    },
    {
      id: 10,
      name: "Multi Band Ligator",
      description: "Multi-band ligation system for hemostasis procedures.",
      category: "Hemostasis",
      manufacturer: "Endosurgery",
      model: "MBL-001",
      image: "/assets/images/multi-band-ligator.jpg",
      price: 450,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        bandsPerSession: "6-10",
        workingLength: "230cm",
        bandSize: "11mm"
      }
    },
    {
      id: 11,
      name: "Sclerotherapy Needle",
      description: "Specialized needles for sclerotherapy procedures.",
      category: "Hemostasis",
      manufacturer: "Endosurgery",
      model: "SN-001",
      image: "/assets/images/sclerotherapy-needle.jpg",
      price: 95,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        needleSize: "25G",
        workingLength: "230cm",
        injectionVolume: "0.5-2ml"
      }
    },

    // SEMS Stenting Products (6)
    {
      id: 12,
      name: "Biliary Stents",
      description: "Biliary stents for SEMS stenting procedures.",
      category: "SEMS Stenting",
      manufacturer: "Endosurgery",
      model: "BS-001",
      image: "/assets/images/biliary-stents.jpg",
      price: 1200,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "8-10mm",
        length: "60-100mm",
        material: "Nitinol"
      }
    },
    {
      id: 13,
      name: "Colon/Rectum Stents",
      description: "Stents for colon and rectum SEMS procedures.",
      category: "SEMS Stenting",
      manufacturer: "Endosurgery",
      model: "CRS-001",
      image: "/assets/images/colon-rectum-stents.jpg",
      price: 1800,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "20-25mm",
        length: "60-120mm",
        material: "Nitinol"
      }
    },
    {
      id: 14,
      name: "Duodenum/Pylorus Stents",
      description: "Stents for duodenum and pylorus SEMS procedures.",
      category: "SEMS Stenting",
      manufacturer: "Endosurgery",
      model: "DPS-001",
      image: "/assets/images/duodenum-pylorus-stents.jpg",
      price: 1500,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "18-22mm",
        length: "60-100mm",
        material: "Nitinol"
      }
    },
    {
      id: 15,
      name: "Esophagus Stents",
      description: "Esophageal stents for SEMS stenting procedures.",
      category: "SEMS Stenting",
      manufacturer: "Endosurgery",
      model: "ES-001",
      image: "/assets/images/esophagus-stents.jpg",
      price: 1600,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "18-24mm",
        length: "80-150mm",
        material: "Nitinol"
      }
    },
    {
      id: 16,
      name: "Gastro-Seal™",
      description: "Gastro-Seal™ system for advanced stenting procedures.",
      category: "SEMS Stenting",
      manufacturer: "Endosurgery",
      model: "GS-001",
      image: "/assets/images/gastro-seal.jpg",
      price: 2200,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "20-25mm",
        length: "100-150mm",
        material: "Nitinol with seal"
      }
    },
    {
      id: 17,
      name: "Trachea/Bronchium Stents",
      description: "Stents for trachea and bronchium SEMS procedures.",
      category: "SEMS Stenting",
      manufacturer: "Endosurgery",
      model: "TBS-001",
      image: "/assets/images/trachea-bronchium-stents.jpg",
      price: 1900,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "12-18mm",
        length: "40-80mm",
        material: "Nitinol"
      }
    },

    // Accessories (3)
    {
      id: 18,
      name: "Cleaning Brush",
      description: "Specialized cleaning brushes for endoscopic equipment.",
      category: "Accessories",
      manufacturer: "Endosurgery",
      model: "CB-001",
      image: "/assets/images/cleaning-brush.jpg",
      price: 45,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        brushSize: "Various",
        material: "Nylon bristles",
        handleLength: "30cm"
      }
    },
    {
      id: 19,
      name: "Disposable Rat Tooth Alligator",
      description: "Disposable rat tooth alligator forceps for procedures.",
      category: "Accessories",
      manufacturer: "Endosurgery",
      model: "RTA-001",
      image: "/assets/images/rat-tooth-alligator.jpg",
      price: 85,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        jawSize: "2.8mm",
        workingLength: "230cm",
        disposable: "Yes"
      }
    },
    {
      id: 20,
      name: "Disposable Bite Block",
      description: "Disposable bite blocks for patient safety during procedures.",
      category: "Accessories",
      manufacturer: "Endosurgery",
      model: "BB-001",
      image: "/assets/images/bite-block.jpg",
      price: 25,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "Standard",
        material: "Medical grade plastic",
        disposable: "Yes"
      }
    },

    // Polypectomy Products (2)
    {
      id: 21,
      name: "Polyp Trap",
      description: "Polyp trap for collection during polypectomy procedures.",
      category: "Polypectomy",
      manufacturer: "Endosurgery",
      model: "PT-001",
      image: "/assets/images/polyp-trap.jpg",
      price: 65,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        capacity: "50ml",
        material: "Medical grade plastic",
        filterSize: "2mm"
      }
    },
    {
      id: 22,
      name: "Polypectomy Snare",
      description: "Advanced polypectomy snares for precise polyp removal.",
      category: "Polypectomy",
      manufacturer: "Endosurgery",
      model: "PS-001",
      image: "/assets/images/polypectomy-snare.jpg",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        wireSize: "0.47mm",
        loopSize: "25mm",
        workingLength: "230cm"
      }
    },

    // Rapid Urease Test Products (2)
    {
      id: 23,
      name: "AMA RUT Expert",
      description: "Professional rapid urease test for H. pylori detection.",
      category: "Rapid Urease Test",
      manufacturer: "Endosurgery",
      model: "RUT-EXP-001",
      image: "/assets/images/ama-rut-expert.jpg",
      price: 35,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        testTime: "5-10 minutes",
        sensitivity: "95%",
        specificity: "98%"
      }
    },
    {
      id: 24,
      name: "AMA RUT Pro",
      description: "Advanced rapid urease test for professional diagnostic use.",
      category: "Rapid Urease Test",
      manufacturer: "Endosurgery",
      model: "RUT-PRO-001",
      image: "/assets/images/ama-rut-pro.jpg",
      price: 45,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        testTime: "3-5 minutes",
        sensitivity: "97%",
        specificity: "99%"
      }
    },

    // Obesity Management Products (1)
    {
      id: 25,
      name: "Adjustable Intragastric Balloon",
      description: "Adjustable intragastric balloon system for weight management procedures.",
      category: "Obesity Management",
      manufacturer: "Endosurgery",
      model: "AIB-001",
      image: "/assets/images/adjustable-intragastric-balloon.jpg",
      price: 8500,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        volume: "400-700ml adjustable",
        duration: "6-12 months",
        material: "Medical grade silicone"
      }
    },

    // Endo-Urology Access Products (3)
    {
      id: 26,
      name: "Dual Lumen Catheter",
      description: "Dual lumen catheter for endo-urology access procedures.",
      category: "Endo-Urology Access",
      manufacturer: "Endosurgery",
      model: "DLC-001",
      image: "/assets/images/no_image.png",
      price: 280,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "6-8Fr",
        workingLength: "40cm",
        material: "Polyurethane"
      }
    },
    {
      id: 27,
      name: "Guide Wire",
      description: "High-quality guide wire for endo-urology navigation.",
      category: "Endo-Urology Access",
      manufacturer: "Endosurgery",
      model: "GW-URO-001",
      image: "/assets/images/no_image.png",
      price: 95,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        diameter: "0.035 inch",
        length: "150cm",
        material: "Nitinol"
      }
    },
    {
      id: 28,
      name: "Ureteral Catheter",
      description: "Professional ureteral catheter for endo-urology procedures.",
      category: "Endo-Urology Access",
      manufacturer: "Endosurgery",
      model: "UC-001",
      image: "/assets/images/no_image.png",
      price: 120,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "5-7Fr",
        workingLength: "70cm",
        material: "Polyurethane"
      }
    },

    // Endo-Urology Dilation Products (12)
    {
      id: 29,
      name: "Dilatron™ – Amplatz Dilator Set",
      description: "Amplatz dilator set for endo-urology dilation procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "ADS-001",
      image: "/assets/images/no_image.png",
      price: 850,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "8-30Fr",
        setSize: "12 pieces",
        material: "Teflon"
      }
    },
    {
      id: 30,
      name: "Fascial Dilator",
      description: "Fascial dilator for endo-urology procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "FD-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "8-24Fr",
        workingLength: "20cm",
        material: "Metal"
      }
    },
    {
      id: 31,
      name: "Filiform Urethral Dilator",
      description: "Filiform urethral dilator for urethral dilation procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "FUD-001",
      image: "/assets/images/no_image.png",
      price: 220,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "3-6Fr",
        workingLength: "25cm",
        material: "Silk"
      }
    },
    {
      id: 32,
      name: "Metal Dilator",
      description: "Metal dilator for endo-urology procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "MD-001",
      image: "/assets/images/no_image.png",
      price: 150,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "8-30Fr",
        workingLength: "20cm",
        material: "Stainless steel"
      }
    },
    {
      id: 33,
      name: "Nephrostomy Balloon Dilator",
      description: "Nephrostomy balloon dilator for kidney procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "NBD-001",
      image: "/assets/images/no_image.png",
      price: 450,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        balloonSize: "8-12mm",
        workingLength: "25cm",
        inflationVolume: "2-4ml"
      }
    },
    {
      id: 34,
      name: "Nottingham One Step Catheter",
      description: "Nottingham one step catheter for endo-urology procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "NOSC-001",
      image: "/assets/images/no_image.png",
      price: 320,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "8-12Fr",
        workingLength: "25cm",
        material: "Polyurethane"
      }
    },
    {
      id: 35,
      name: "S Shape Dilator Set",
      description: "S shape dilator set for endo-urology procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "SDS-001",
      image: "/assets/images/no_image.png",
      price: 680,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "8-24Fr",
        setSize: "9 pieces",
        material: "Teflon"
      }
    },
    {
      id: 36,
      name: "Screw Dilator",
      description: "Screw dilator for endo-urology procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "SD-001",
      image: "/assets/images/no_image.png",
      price: 280,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "8-24Fr",
        workingLength: "20cm",
        material: "Metal"
      }
    },
    {
      id: 37,
      name: "Ureteral Dilator",
      description: "Ureteral dilator for ureteral dilation procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "UD-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "6-12Fr",
        workingLength: "70cm",
        material: "Teflon"
      }
    },
    {
      id: 38,
      name: "Ureteral Balloon Dilator",
      description: "Ureteral balloon dilator for ureteral procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "UBD-001",
      image: "/assets/images/no_image.png",
      price: 420,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        balloonSize: "6-10mm",
        workingLength: "70cm",
        inflationVolume: "2-4ml"
      }
    },
    {
      id: 39,
      name: "Urethral Dilator Set",
      description: "Urethral dilator set for urethral procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "UDS-001",
      image: "/assets/images/no_image.png",
      price: 350,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "8-30Fr",
        setSize: "12 pieces",
        material: "Teflon"
      }
    },
    {
      id: 40,
      name: "Urox™ – Ureteral Access Sheath",
      description: "Urox ureteral access sheath for endo-urology procedures.",
      category: "Endo-Urology Dilation",
      manufacturer: "Endosurgery",
      model: "UAS-001",
      image: "/assets/images/no_image.png",
      price: 580,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        sizes: "9-16Fr",
        workingLength: "35cm",
        material: "Teflon"
      }
    },

    // Endo-Urology Drainage Products (7)
    {
      id: 41,
      name: "Cystocath",
      description: "Cystocath for bladder drainage procedures.",
      category: "Endo-Urology Drainage",
      manufacturer: "Endosurgery",
      model: "CC-001",
      image: "/assets/images/no_image.png",
      price: 85,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "12-16Fr",
        workingLength: "25cm",
        material: "Silicone"
      }
    },
    {
      id: 42,
      name: "Double \"J\" Stent",
      description: "Double J stent for ureteral drainage.",
      category: "Endo-Urology Drainage",
      manufacturer: "Endosurgery",
      model: "DJS-001",
      image: "/assets/images/no_image.png",
      price: 280,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "4-8Fr",
        length: "22-30cm",
        material: "Polyurethane"
      }
    },
    {
      id: 43,
      name: "Endopyelotomy Stent",
      description: "Endopyelotomy stent for kidney procedures.",
      category: "Endo-Urology Drainage",
      manufacturer: "Endosurgery",
      model: "EPS-001",
      image: "/assets/images/no_image.png",
      price: 320,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "6-8Fr",
        length: "24-28cm",
        material: "Polyurethane"
      }
    },
    {
      id: 44,
      name: "PCN Catheter",
      description: "PCN catheter for percutaneous nephrostomy procedures.",
      category: "Endo-Urology Drainage",
      manufacturer: "Endosurgery",
      model: "PCN-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "8-12Fr",
        workingLength: "25cm",
        material: "Polyurethane"
      }
    },
    {
      id: 45,
      name: "Re-entry Malecot Catheter",
      description: "Re-entry Malecot catheter for drainage procedures.",
      category: "Endo-Urology Drainage",
      manufacturer: "Endosurgery",
      model: "RMC-001",
      image: "/assets/images/no_image.png",
      price: 220,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "12-16Fr",
        workingLength: "25cm",
        material: "Silicone"
      }
    },
    {
      id: 46,
      name: "Suprapubic / Malecot Catheter",
      description: "Suprapubic Malecot catheter for bladder drainage.",
      category: "Endo-Urology Drainage",
      manufacturer: "Endosurgery",
      model: "SMC-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "12-16Fr",
        workingLength: "25cm",
        material: "Silicone"
      }
    },
    {
      id: 47,
      name: "Urethral Stent",
      description: "Urethral stent for urethral drainage procedures.",
      category: "Endo-Urology Drainage",
      manufacturer: "Endosurgery",
      model: "US-001",
      image: "/assets/images/no_image.png",
      price: 250,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "16-20Fr",
        length: "8-12cm",
        material: "Silicone"
      }
    },

    // Endo-Urology Stone Management Products (10)
    {
      id: 48,
      name: "Balloon Shape",
      description: "Balloon shape device for stone management procedures.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "BS-SM-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        balloonSize: "8-12mm",
        workingLength: "70cm",
        inflationVolume: "2-4ml"
      }
    },
    {
      id: 49,
      name: "G-Paw",
      description: "G-Paw device for stone management procedures.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "GP-001",
      image: "/assets/images/no_image.png",
      price: 220,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        basketSize: "1.5-2.5cm",
        workingLength: "70cm",
        material: "Nitinol"
      }
    },
    {
      id: 50,
      name: "Helical with Tip",
      description: "Helical device with tip for stone management.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "HT-001",
      image: "/assets/images/no_image.png",
      price: 280,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        basketSize: "1.5-2.5cm",
        workingLength: "70cm",
        tipLength: "5mm"
      }
    },
    {
      id: 51,
      name: "Perk Basket",
      description: "Perk basket for stone retrieval procedures.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "PB-001",
      image: "/assets/images/no_image.png",
      price: 320,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        basketSize: "1.5-2.5cm",
        workingLength: "70cm",
        material: "Nitinol"
      }
    },
    {
      id: 52,
      name: "Segura with Tip",
      description: "Segura device with tip for stone management.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "ST-001",
      image: "/assets/images/no_image.png",
      price: 350,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        basketSize: "1.5-2.5cm",
        workingLength: "70cm",
        tipLength: "5mm"
      }
    },
    {
      id: 53,
      name: "Segura Tipless",
      description: "Segura tipless device for stone management.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "STT-001",
      image: "/assets/images/no_image.png",
      price: 320,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        basketSize: "1.5-2.5cm",
        workingLength: "70cm",
        material: "Nitinol"
      }
    },
    {
      id: 54,
      name: "Stone Grasper",
      description: "Stone grasper for stone retrieval procedures.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "SG-001",
      image: "/assets/images/no_image.png",
      price: 280,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        jawSize: "2.8mm",
        workingLength: "70cm",
        material: "Stainless steel"
      }
    },
    {
      id: 55,
      name: "Stone Stop",
      description: "Stone stop device for stone management procedures.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "SS-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "3-5mm",
        workingLength: "70cm",
        material: "Nitinol"
      }
    },
    {
      id: 56,
      name: "URS Forceps",
      description: "URS forceps for ureteroscopic procedures.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "URSF-001",
      image: "/assets/images/no_image.png",
      price: 220,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        jawSize: "2.8mm",
        workingLength: "70cm",
        material: "Stainless steel"
      }
    },
    {
      id: 57,
      name: "X-Circle",
      description: "X-Circle device for stone management procedures.",
      category: "Endo-Urology Stone Management",
      manufacturer: "Endosurgery",
      model: "XC-001",
      image: "/assets/images/no_image.png",
      price: 280,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        basketSize: "1.5-2.5cm",
        workingLength: "70cm",
        material: "Nitinol"
      }
    },

    // Endo-Urology TURP Products (7)
    {
      id: 58,
      name: "Biopsy Gun",
      description: "Biopsy gun for tissue sampling procedures.",
      category: "Endo-Urology TURP",
      manufacturer: "Endosurgery",
      model: "BG-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        needleSize: "18G",
        workingLength: "25cm",
        throwLength: "15-22mm"
      }
    },
    {
      id: 59,
      name: "Chiba Needle",
      description: "Chiba needle for endo-urology procedures.",
      category: "Endo-Urology TURP",
      manufacturer: "Endosurgery",
      model: "CN-001",
      image: "/assets/images/no_image.png",
      price: 85,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        needleSize: "22G",
        workingLength: "15cm",
        material: "Stainless steel"
      }
    },
    {
      id: 60,
      name: "Evacuator",
      description: "Evacuator for TURP procedures.",
      category: "Endo-Urology TURP",
      manufacturer: "Endosurgery",
      model: "EV-001",
      image: "/assets/images/no_image.png",
      price: 120,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        capacity: "100ml",
        material: "Medical grade plastic",
        connection: "Luer lock"
      }
    },
    {
      id: 61,
      name: "IP Needle",
      description: "IP needle for endo-urology procedures.",
      category: "Endo-Urology TURP",
      manufacturer: "Endosurgery",
      model: "IPN-001",
      image: "/assets/images/no_image.png",
      price: 95,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        needleSize: "20G",
        workingLength: "15cm",
        material: "Stainless steel"
      }
    },
    {
      id: 62,
      name: "Penile Clamp",
      description: "Penile clamp for urological procedures.",
      category: "Endo-Urology TURP",
      manufacturer: "Endosurgery",
      model: "PC-001",
      image: "/assets/images/no_image.png",
      price: 65,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "Adjustable",
        material: "Silicone",
        pressure: "Adjustable"
      }
    },
    {
      id: 63,
      name: "Pathfinder",
      description: "Pathfinder device for endo-urology procedures.",
      category: "Endo-Urology TURP",
      manufacturer: "Endosurgery",
      model: "PF-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "3-5Fr",
        workingLength: "25cm",
        material: "Teflon"
      }
    },
    {
      id: 64,
      name: "TURP Loop / Electrodes",
      description: "TURP loop and electrodes for transurethral procedures.",
      category: "Endo-Urology TURP",
      manufacturer: "Endosurgery",
      model: "TLE-001",
      image: "/assets/images/no_image.png",
      price: 280,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        loopSize: "24-26Fr",
        workingLength: "25cm",
        material: "Tungsten"
      }
    },

    // Laparoscopy Access Products (1)
    {
      id: 65,
      name: "Multichannel Superior Access Port",
      description: "Multichannel superior access port for laparoscopic procedures.",
      category: "Laparoscopy Access",
      manufacturer: "Endosurgery",
      model: "MSAP-001",
      image: "/assets/images/no_image.png",
      price: 450,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "12mm",
        channels: "3",
        material: "Medical grade plastic"
      }
    },

    // Laparoscopy Accessories Products (1)
    {
      id: 66,
      name: "Disposable Camera Cover",
      description: "Disposable camera cover for laparoscopic procedures.",
      category: "Laparoscopy Accessories",
      manufacturer: "Endosurgery",
      model: "DCC-001",
      image: "/assets/images/no_image.png",
      price: 35,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        size: "10mm",
        material: "Medical grade plastic",
        disposable: "Yes"
      }
    },

    // Wound Drainage Products (1)
    {
      id: 67,
      name: "Close Wound Drainage System Kit",
      description: "Close wound drainage system kit for surgical procedures.",
      category: "Wound Drainage",
      manufacturer: "Endosurgery",
      model: "CWDS-001",
      image: "/assets/images/no_image.png",
      price: 180,
      availability: "In Stock",
      certifications: ["CE", "GDPMD"],
      specifications: {
        catheterSize: "10-12Fr",
        length: "25cm",
        material: "Silicone"
      }
    }
  ];

  // Filter products based on category, search query, and other filters
  useEffect(() => {
    let filtered = [...mockProducts];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(product => 
        product?.category === selectedCategory
      );
    }

    // Filter by search query
    if (searchQuery) {
      console.log('Filtering by search query:', searchQuery);
      filtered = filtered?.filter(product =>
        product?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.manufacturer?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
      console.log('Filtered products count:', filtered.length);
    }

    // Sort products
    switch (sortBy) {
      case 'name':
        filtered?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      case 'category':
        filtered?.sort((a, b) => a?.category?.localeCompare(b?.category));
        break;
      case 'newest':
        filtered?.sort((a, b) => b?.id - a?.id);
        break;
      case 'price-low':
        filtered?.sort((a, b) => (a?.price || 0) - (b?.price || 0));
        break;
      case 'price-high':
        filtered?.sort((a, b) => (b?.price || 0) - (a?.price || 0));
        break;
      default:
        // Keep original order for relevance
        break;
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, searchQuery, sortBy]);

  const handleSearch = (query) => {
    console.log('Search query received:', query);
    setSearchQuery(query);
    setLoading(true);
    // Simulate API call delay
    setTimeout(() => setLoading(false), 500);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setLoading(true);
    // Simulate API call delay
    setTimeout(() => setLoading(false), 300);
  };

  const handleFilterChange = (filters) => {
    // Handle advanced filter changes
    console.log('Filters changed:', filters);
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-8xl mx-auto px-8 py-8">
          {/* Breadcrumb */}
          <Breadcrumb />

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Product Portfolio
                </h1>
                <p className="text-text-secondary text-lg">
                  Explore our comprehensive range of endoscopic and surgical medical devices
                </p>
              </div>
              
              {/* Quick Stats */}
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">67</div>
                  <div className="text-xs text-text-secondary uppercase tracking-wide">Products</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-success">67</div>
                  <div className="text-xs text-text-secondary uppercase tracking-wide">GDPMD Certified</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-warning">18</div>
                  <div className="text-xs text-text-secondary uppercase tracking-wide">Categories</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <ProductSearch
              onSearch={handleSearch}
              onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
            />
          </motion.div>

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Filters */}
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:w-80 flex-shrink-0"
            >
              {/* Category Filter */}
              <div className="mb-6">
                <CategoryFilter
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                />
              </div>

              {/* Advanced Filters */}
              <ProductFilter
                isOpen={isFilterOpen}
                onToggle={() => setIsFilterOpen(!isFilterOpen)}
                onFilterChange={handleFilterChange}
              />
            </motion.aside>

            {/* Main Content Area */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="flex-1 min-w-0"
            >
              <ProductGrid
                products={filteredProducts}
                viewMode={viewMode}
                loading={loading}
                sortBy={sortBy}
                onSortChange={setSortBy}
              />
            </motion.div>
          </div>

          {/* Bottom CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8 border border-primary/10"
          >
            <div className="text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MessageCircle" size={24} className="text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                Need Help Finding the Right Product?
              </h3>
              <p className="text-text-secondary mb-6">
                Our medical device specialists are here to help you find the perfect solution for your clinical needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => window.location.href = '/contact'}
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
                >
                  <Icon name="Phone" size={16} className="mr-2" />
                  Contact Our Experts
                </button>
                <button
                  onClick={() => window.location.href = '/about-us'}
                  className="inline-flex items-center justify-center px-6 py-3 border border-border text-text-primary rounded-lg hover:bg-muted transition-colors duration-200"
                >
                  <Icon name="Info" size={16} className="mr-2" />
                  Learn More About Us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default ProductPortfolio;
